import { TextInput } from 'deep-chat/dist/types/textInput';
import { TranslateService } from '@ngx-translate/core';
import { AI_COLORS } from './wh-ai-colors';

export type WhAiStyleConfig = {
  textInput: TextInput;
  messageStyles: Record<string, unknown>;
  inputAreaStyle: Record<string, string>;
  focusMode: Record<string, boolean>;
  auxiliaryStyle: string;
  submitButtonStyles: Record<string, unknown>;
};

export function buildWhAiStyleConfig(
  translate: TranslateService,
): WhAiStyleConfig {
  return {
    textInput: {
      styles: {
        text: { color: AI_COLORS.text },
        container: {
          maxHeight: '210px',
          backgroundColor: 'transparent',
          height: '88px',
          width: '90%',
          borderRadius: '16px',
          padding: '12px 8px',
          border: `1px solid ${AI_COLORS.muted}`,
        },
        focus: {
          border: `2px solid ${AI_COLORS.accent}`,
        },
      },
      placeholder: {
        text: translate.instant('app.ai.askSomething.input.placeholder'),
        style: { color: AI_COLORS.textMuted },
      },
    } as TextInput,

    messageStyles: {
      error: {
        bubble: {
          backgroundColor: 'transparent',
          color: AI_COLORS.text,
          fontSize: '15px',
        },
      },
      loading: {
        styles: {},
        message: {
          styles: {
            bubble: {
              backgroundColor: 'transparent',
              border: '0px solid transparent',
              padding: '0',
              boxShadow: 'none',
            },
          },
          html: `<div></div>`,
        },
      },
      default: {
        shared: {
          bubble: {
            color: 'white',
            lineHeight: 1.9,
            padding: '16px',
          },
        },
        ai: {
          bubble: {
            position: 'relative',
            backgroundColor: '#ebebeb',
            color: AI_COLORS.text,
            maxWidth: '90%',
            borderRadius: '16px',
          },
        },
        user: {
          bubble: {
            backgroundColor: 'transparent',
            border: `2px solid ${AI_COLORS.primary}`,
            padding: '8px 16px',
            borderRadius: '16px',
            color: AI_COLORS.text,
          },
        },
      },
      html: {
        shared: {
          bubble: {
            color: AI_COLORS.text,
            lineHeight: 1.7,
            padding: '16px',
          },
        },
        ai: {
          bubble: {
            position: 'relative',
            backgroundColor: '#ebebeb',
            color: AI_COLORS.text,
            maxWidth: '90%',
            borderRadius: '16px',
          },
        },
      },
    },

    inputAreaStyle: { borderTop: '0px solid #e8e8e8' },

    focusMode: { smoothScroll: true },

    auxiliaryStyle: `
      ::-webkit-scrollbar {
        width: 2px;
        height: 10px;
      }
      ::-webkit-scrollbar-thumb {
        border-radius: 5px;
      }

      .message-bubble.ai-message {
        position: relative;
        border-radius: 16px;
      }

      .message-bubble.ai-message:not(.deep-chat-loading-message-bubble)::before {
        content: '';
        position: absolute;
        inset: 0;
        padding: 2px;
        border-radius: inherit;
        background: linear-gradient(
          90deg,
          var(--wh-ai-primary),
          var(--wh-ai-accent)
        );
        pointer-events: none;

        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
                mask-composite: exclude;
      }

      .wh-ai-response {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .wh-ai-response-text {
        color: ${AI_COLORS.text};
      }

      .wh-ai-suggestions-header-container {
        display: flex;
        align-items: center;
        gap: 8px;

        svg {
          fill: ${AI_COLORS.textMuted};
        }
      }

      .wh-ai-suggestions-header {
        font-size: 16px;
        font-weight: 600;
        color: ${AI_COLORS.textMuted};
      }

      .wh-ai-sources, 
      .wh-ai-suggestions {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .wh-ai-sources {
        position: relative;
        align-items: flex-start;
      }

      .wh-ai-suggestions {
        .wh-ai-source-title {
          font-size: 14px;
          font-weight: 400;
        }

        .wh-ai-source-card {
          width: fit-content;
        }
      }

      .wh-ai-sources--fade-in {
        animation: sourcesFadeIn 0.3s ease-out;
      }

      .wh-ai-sources--fade-in .wh-ai-source-item {
        opacity: 0;
        animation: sourceItemFadeIn 0.3s ease-out forwards;
      }

      .wh-ai-sources-extracting {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 12px;
        font-size: 13px;
        color: ${AI_COLORS.textMuted};
        animation: sourcesFadeIn 0.3s ease-out;
      }

      .wh-ai-extracting-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${AI_COLORS.accent};
        flex-shrink: 0;
        animation: timelinePulse 1.4s ease-in-out infinite;
      }

      .wh-ai-sources-title {
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: ${AI_COLORS.textMuted};
      }

      .wh-ai-sources-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .wh-ai-response-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .wh-ai-response-date {
        font-size: 11px;
        color: ${AI_COLORS.textMuted};
      }

      .wh-ai-feedback {
        display: flex;
        align-items: center;
        gap: 8px;
        justify-content: flex-end;
      }

      .wh-ai-feedback-button {
        border-radius: 999px;
        border: 2px solid ${AI_COLORS.muted};
        background: rgba(255, 255, 255, 0.6);
        color: ${AI_COLORS.text};
        padding: 6px;
        transition: 0.2s ease-in-out;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        svg {
          fill : ${AI_COLORS.text};
        }
      }

      .wh-ai-feedback-button:hover {
        cursor: pointer;
        border: 2px solid ${AI_COLORS.accent};
        color: ${AI_COLORS.text};
      }

      .wh-ai-feedback-button.is-active {
        background: ${AI_COLORS.accent};
        border: 2px solid ${AI_COLORS.accent};
        color: #fff;
        svg {
            fill : #fff;
        }
      }

      .wh-ai-feedback-button.is-hidden {
        display: none;
      }

      .wh-ai-source-item {
        margin: 0;
      }

      .wh-ai-source-index {
        background: ${AI_COLORS.muted};
        color: ${AI_COLORS.text};
        font-weight: 700;
        border-radius: 999px;
        min-width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
      }

      .wh-ai-source-link,
      .wh-ai-source-card {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 12px;
        border: 2px solid ${AI_COLORS.muted};
        background: rgba(255, 255, 255, 0.6);
        color: ${AI_COLORS.text};
        text-decoration: none;
        text-align: left;
        transition: 0.2s ease-in-out;
        position: relative;
      }

      .wh-ai-source-link--inline {
        display: inline-flex;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
        flex-wrap: wrap;
        vertical-align: middle;
        margin: 2px 4px 2px 0;
        padding: 4px 10px;
        border-radius: 12px;
        gap: 6px;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      .wh-ai-source-link.is-loading,
      .wh-ai-source-card.is-loading {
        pointer-events: none;
        opacity: 0.7;
      }

      .wh-ai-source-link.is-loading .wh-ai-source-index,
      .wh-ai-source-card.is-loading .wh-ai-source-index {
        color: transparent;
        position: relative;
      }

      .wh-ai-source-link.is-loading .wh-ai-source-index::after,
      .wh-ai-source-card.is-loading .wh-ai-source-index::after {
        content: '';
        position: absolute;
        inset: 0;
        margin: auto;
        width: 12px;
        height: 12px;
        border: 2px solid ${AI_COLORS.muted};
        border-top-color: ${AI_COLORS.accent};
        border-radius: 50%;
        animation: sourceSpinner 0.6s linear infinite;
      }

      @keyframes sourceSpinner {
        to { transform: rotate(360deg); }
      }

      .wh-ai-source-link:hover {
        cursor: pointer;
        border: 2px solid ${AI_COLORS.accent};
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);
        transform: translateY(-1px);
      }

      .wh-ai-source-card:hover {
        cursor: pointer;
        border: 2px solid ${AI_COLORS.accent};
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);
        transform: translateY(-1px);
      }

      .wh-ai-source-card-header {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .wh-ai-source-title {
        font-size: 13px;
        font-weight: 600;
        color: ${AI_COLORS.text};
      }

      .wh-welcome-message {
        animation: fadeIn 0.6s ease-out forwards;
      }
      
      @keyframes fadeIn {
        0% {
          transform: translateY(32px);
          opacity: 0;
        }
        80% {
          transform: translateY(0);
          opacity: 0.6;
        }
        100% {
          transform: translateY(0);
          opacity: 1;
        }
      }
      @keyframes shimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
      @keyframes dotPulse {
        0%, 100% { opacity: 0.2; }
        50% { opacity: 1; }
      }
      @keyframes sourcesFadeIn {
        0% {
          opacity: 0;
          transform: translateY(4px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes sourceItemFadeIn {
        0% {
          opacity: 0;
          transform: translateX(-8px);
        }
        100% {
          opacity: 1;
          transform: translateX(0);
        }
      }
      .thinking-loader {
        display: flex;
        align-items: center;
        font-size: 14px;
      }
      .thinking-text {
        background: linear-gradient(
          90deg,
          ${AI_COLORS.textMuted} 25%,
          ${AI_COLORS.text} 50%,
          ${AI_COLORS.textMuted} 75%
        );
        background-size: 400% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: shimmer 2.5s linear infinite;
      }
      .dots {
        display: inline-flex;
        margin-left: 2px;
      }
      .dots span {
        background: linear-gradient(
          90deg,
          ${AI_COLORS.textMuted} 25%,
          ${AI_COLORS.text} 50%,
          ${AI_COLORS.textMuted} 75%
        );
        background-size: 400% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: shimmer 2.5s linear infinite;
      }
      .dots span:nth-child(1) { animation-delay: 0.15s; }
      .dots span:nth-child(2) { animation-delay: 0.3s; }
      .dots span:nth-child(3) { animation-delay: 0.45s; }

      .message-bubble:has(.wh-ai-timeline) {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 4px 0 !important;
      }

      .message-bubble:has(.wh-ai-timeline)::before {
        display: none !important;
      }

      .wh-ai-timeline {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 4px 0;
      }

      .wh-ai-timeline-step {
        display: flex;
        align-items: center;
        gap: 10px;
        position: relative;
        padding: 4px 0;
        padding-left: 4px;
        animation: timelineFadeIn 0.3s ease-out;
      }

      .wh-ai-timeline-step:not(:last-child)::after {
        content: '';
        position: absolute;
        left: 7px;
        top: 20px;
        bottom: -4px;
        width: 2px;
        background: ${AI_COLORS.muted};
      }

      .wh-ai-timeline-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
        z-index: 1;
        transition: background 0.3s ease;
      }

      .wh-ai-timeline-step.is-done .wh-ai-timeline-dot {
        background: ${AI_COLORS.muted};
      }

      .wh-ai-timeline-step.is-active .wh-ai-timeline-dot {
        background: ${AI_COLORS.accent};
        animation: timelinePulse 1.4s ease-in-out infinite;
      }

      .wh-ai-timeline-step.is-done {
        opacity: 0.5;
      }

      .wh-ai-timeline-step.is-done .wh-ai-timeline-label {
        color: ${AI_COLORS.textMuted};
        font-size: 13px;
      }

      .wh-ai-timeline-step.is-active .wh-ai-timeline-label {
        font-size: 13px;
        background: linear-gradient(
          90deg,
          ${AI_COLORS.textMuted} 25%,
          ${AI_COLORS.text} 50%,
          ${AI_COLORS.textMuted} 75%
        );
        background-size: 400% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: shimmer 2.5s linear infinite;
      }

      @keyframes timelinePulse {
        0%, 100% { opacity: 0.4; transform: scale(0.8); }
        50% { opacity: 1; transform: scale(1); }
      }

      @keyframes timelineFadeIn {
        0% { opacity: 0; transform: translateY(4px); }
        100% { opacity: 1; transform: translateY(0); }
      }
    `,

    submitButtonStyles: {
      submit: {
        container: {
          default: {
            backgroundColor: AI_COLORS.accent,
            borderRadius: '50%',
            padding: '8px',
            marginBottom: '12px',
            marginRight: '8px',
          },
          hover: {
            backgroundColor: AI_COLORS.accent,
            opacity: '0.85',
            cursor: 'pointer',
          },
          click: {
            backgroundColor: AI_COLORS.accent,
          },
        },
        svg: {
          styles: {
            default: {
              filter:
                'brightness(0) invert(1) drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white)',
            },
          },
        },
      },
      disabled: {
        container: {
          default: {
            backgroundColor: AI_COLORS.accent,
            borderRadius: '50%',
            padding: '8px',
            opacity: 0.5,
            marginBottom: '12px',
            marginRight: '8px',
          },
        },
        svg: {
          styles: {
            default: {
              filter:
                'brightness(0) invert(1) drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white)',
            },
          },
        },
      },
      loading: {
        container: {
          default: {
            backgroundColor: 'transparent',
            borderRadius: '50%',
            padding: '8px',
            marginBottom: '12px',
            marginRight: '8px',
          },
        },
        svg: {
          styles: {
            default: {
              filter:
                'brightness(0) invert(1) drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white)',
            },
          },
        },
      },
      stop: {
        container: {
          default: {
            backgroundColor: 'transparent',
            borderRadius: '50%',
            padding: '8px',
            marginBottom: '12px',
            marginRight: '8px',
          },
        },
      },
      position: 'inside-right',
    },
  };
}
